import { fieldTypeOf } from '../../field-types';
import { holdsFields, parentOf } from '../../format/elements';
import type { FieldElement, FormDefinition } from '../../format/form-definition';
import { VALIDATION_KINDS, type Validation, type ValidationKind } from '../../format/pieces';
import { ValueKind } from '../../field-types/card';
import { mayBeBoundOf, referableFields } from '../../format/references';

export const validationOf = <K extends ValidationKind>(
  field: FieldElement,
  kind: K,
): ValidationOfKind<K> | undefined => {
  return field.validations?.find((validation): validation is ValidationOfKind<K> => validation.kind === kind);
};

type ValidationOfKind<K extends ValidationKind> = Extract<Validation, { kind: K }>;

export const ruleKindsOf = (form: FormDefinition, field: FieldElement): ValidationKind[] => {
  const card = fieldTypeOf(field);
  const parent = parentOf(form, field.id);
  const inGroup = parent?.kind === 'field' && holdsFields(parent);
  return VALIDATION_KINDS.filter(kind => card.validations.includes(kind) && (kind !== 'unique' || inGroup));
};

export const requiredName = (field: FieldElement): string => {
  return fieldTypeOf(field).valueKind === 'boolean' ? 'Must be checked' : 'Required';
};

export const ruleName = (field: FieldElement, kind: ValidationKind): string => {
  switch (kind) {
    case 'required':
      return requiredName(field);
    case 'unique':
      return 'No repeats across rows';
    case 'range':
      return rangeWords(field).name;
    case 'pattern':
      return 'Pattern';
  }
};

type BoundsKind = 'count' | 'number' | 'date';

export interface RangeWords {
  name: string;
  min: string;
  max: string;
  bounds: BoundsKind;
}

const COUNT_ENDS = { min: 'From', max: 'To', bounds: 'count' } as const;

const RANGE_WORDS: Partial<Record<ValueKind, RangeWords>> = {
  text: { name: 'Length, characters', ...COUNT_ENDS },
  options: { name: 'How many to select', ...COUNT_ENDS },
  files: { name: 'Number of files', ...COUNT_ENDS },
  rows: { name: 'Rows', ...COUNT_ENDS },
  number: { name: 'Values allowed', min: 'At least', max: 'At most', bounds: 'number' },
  date: { name: 'Dates allowed', min: 'Not earlier than', max: 'Not later than', bounds: 'date' },
};

export const rangeWords = (field: FieldElement): RangeWords => {
  return RANGE_WORDS[fieldTypeOf(field).valueKind] ?? { name: 'Range', ...COUNT_ENDS };
};

export const boundFields = (form: FormDefinition, field: FieldElement): FieldElement[] => {
  return referableFields(form, field.id, 'bound')
    .filter(referable => !referable.problem && mayBeBoundOf(field, referable.field))
    .map(referable => referable.field);
};
